export interface Institution {
  id: string;
  name: string;
  aliases?: string[];
  country: 'CA' | 'US' | 'INT';
  type: 'bank' | 'credit_union' | 'investment' | 'insurance' | 'other';
}

export const majorInstitutions: Institution[] = [
  // Canadian Banks
  { id: 'td', name: 'TD Canada Trust', aliases: ['TD', 'TD Bank', 'Toronto-Dominion'], country: 'CA', type: 'bank' },
  { id: 'rbc', name: 'Royal Bank of Canada', aliases: ['RBC', 'Royal Bank'], country: 'CA', type: 'bank' },
  { id: 'bmo', name: 'Bank of Montreal', aliases: ['BMO'], country: 'CA', type: 'bank' },
  { id: 'scotiabank', name: 'Scotiabank', aliases: ['Bank of Nova Scotia'], country: 'CA', type: 'bank' },
  { id: 'cibc', name: 'CIBC', aliases: ['Canadian Imperial Bank of Commerce'], country: 'CA', type: 'bank' },
  { id: 'national-bank', name: 'National Bank of Canada', aliases: ['National Bank'], country: 'CA', type: 'bank' },
  { id: 'desjardins', name: 'Desjardins', aliases: ['Caisse Desjardins'], country: 'CA', type: 'credit_union' },
  { id: 'tangerine', name: 'Tangerine Bank', aliases: ['Tangerine'], country: 'CA', type: 'bank' },
  { id: 'pc-financial', name: 'PC Financial', aliases: ['PC Bank'], country: 'CA', type: 'bank' },
  { id: 'simplii', name: 'Simplii Financial', aliases: ['Simplii'], country: 'CA', type: 'bank' },
  
  // Canadian Investment Firms
  { id: 'questrade', name: 'Questrade', country: 'CA', type: 'investment' },
  { id: 'wealthsimple', name: 'Wealthsimple', country: 'CA', type: 'investment' },
  { id: 'td-waterhouse', name: 'TD Waterhouse', aliases: ['TD Direct Investing'], country: 'CA', type: 'investment' },
  { id: 'rbc-direct', name: 'RBC Direct Investing', aliases: ['RBC DI'], country: 'CA', type: 'investment' },
  { id: 'bmo-investorline', name: 'BMO InvestorLine', country: 'CA', type: 'investment' },
  { id: 'scotia-itrade', name: 'Scotia iTRADE', country: 'CA', type: 'investment' },
  { id: 'cibc-investor', name: 'CIBC Investor\'s Edge', country: 'CA', type: 'investment' },
  
  // US Banks
  { id: 'chase', name: 'Chase Bank', aliases: ['JP Morgan Chase'], country: 'US', type: 'bank' },
  { id: 'wells-fargo', name: 'Wells Fargo', country: 'US', type: 'bank' },
  { id: 'bank-of-america', name: 'Bank of America', aliases: ['BofA'], country: 'US', type: 'bank' },
  { id: 'citibank', name: 'Citibank', aliases: ['Citi'], country: 'US', type: 'bank' },
  { id: 'us-bank', name: 'U.S. Bank', country: 'US', type: 'bank' },
  { id: 'pnc', name: 'PNC Bank', country: 'US', type: 'bank' },
  { id: 'capital-one', name: 'Capital One', country: 'US', type: 'bank' },
  { id: 'american-express', name: 'American Express', aliases: ['Amex'], country: 'US', type: 'bank' },
  
  // US Investment Firms
  { id: 'fidelity', name: 'Fidelity Investments', aliases: ['Fidelity'], country: 'US', type: 'investment' },
  { id: 'vanguard', name: 'Vanguard', country: 'US', type: 'investment' },
  { id: 'schwab', name: 'Charles Schwab', aliases: ['Schwab'], country: 'US', type: 'investment' },
  { id: 'etrade', name: 'E*TRADE', country: 'US', type: 'investment' },
  { id: 'robinhood', name: 'Robinhood', country: 'US', type: 'investment' },
  { id: 'td-ameritrade', name: 'TD Ameritrade', country: 'US', type: 'investment' },
  
  // Cryptocurrency Exchanges
  { id: 'coinbase', name: 'Coinbase', country: 'INT', type: 'investment' },
  { id: 'binance', name: 'Binance', country: 'INT', type: 'investment' },
  { id: 'kraken', name: 'Kraken', country: 'INT', type: 'investment' },
  { id: 'gemini', name: 'Gemini', country: 'INT', type: 'investment' },
  { id: 'crypto-com', name: 'Crypto.com', country: 'INT', type: 'investment' },
  
  // Insurance Companies
  { id: 'sun-life', name: 'Sun Life Financial', aliases: ['Sun Life'], country: 'CA', type: 'insurance' },
  { id: 'manulife', name: 'Manulife Financial', aliases: ['Manulife'], country: 'CA', type: 'insurance' },
  { id: 'great-west', name: 'Great-West Life', country: 'CA', type: 'insurance' },
  { id: 'canada-life', name: 'Canada Life', country: 'CA', type: 'insurance' },
  
  // Other Common Institutions
  { id: 'paypal', name: 'PayPal', country: 'INT', type: 'other' },
  { id: 'stripe', name: 'Stripe', country: 'INT', type: 'other' },
  { id: 'square', name: 'Square', country: 'INT', type: 'other' },
  { id: 'venmo', name: 'Venmo', country: 'US', type: 'other' },
  { id: 'cash-app', name: 'Cash App', country: 'US', type: 'other' },
];

export const searchInstitutions = (query: string, existingInstitutions: string[] = []): Institution[] => {
  const lowerQuery = query.toLowerCase().trim();
  
  if (!lowerQuery) {
    return majorInstitutions.slice(0, 10); // Show top 10 by default
  }
  
  const results: Institution[] = [];
  
  // First, search major institutions
  for (const institution of majorInstitutions) {
    if (institution.name.toLowerCase().includes(lowerQuery) ||
        institution.aliases?.some(alias => alias.toLowerCase().includes(lowerQuery))) {
      results.push(institution);
    }
  }
  
  // Then, search existing user institutions
  for (const existing of existingInstitutions) {
    if (existing.toLowerCase().includes(lowerQuery) &&
        !results.some(r => r.name.toLowerCase() === existing.toLowerCase()) &&
        !majorInstitutions.some(m => m.name.toLowerCase() === existing.toLowerCase())) {
      results.push({
        id: `existing-${existing.toLowerCase().replace(/\s+/g, '-')}`,
        name: existing,
        country: 'CA',
        type: 'other'
      });
    }
  }
  
  return results.slice(0, 15); // Limit results
};
